import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  COOKIE_PREFERENCES_EVENT,
  DEFAULT_COOKIE_CONSENT,
  readCookieConsent,
  writeCookieConsent,
  type CookieConsentPreferences,
} from '@/lib/cookieConsent';

type OptionalCookieKey = 'functional' | 'analytics';

const COOKIE_COPY = {
  it: {
    bannerTitle: 'Preferenze cookie',
    bannerBody:
      'Usiamo cookie necessari per sicurezza, login e funzionamento del servizio. Puoi scegliere se abilitare preferenze funzionali e categorie opzionali.',
    reject: 'Rifiuta tutti',
    customize: 'Gestisci preferenze',
    accept: 'Accetta tutti',
    dialogTitle: 'Gestisci preferenze cookie',
    dialogBody:
      'I cookie necessari restano sempre attivi. Le altre categorie verranno usate solo se abilitate.',
    save: 'Salva preferenze',
    necessary: 'Necessari',
    necessaryBody: 'Login, sicurezza, prevenzione frodi, bilanciamento del carico e pagamento quando richiesto.',
    functional: 'Funzionali',
    functionalBody: 'Memorizzano lingua e preferenze dell’interfaccia per migliorare l’esperienza.',
    analytics: 'Analytics',
    analyticsBody: 'Attiva la diagnostica errori Sentry nel browser (replay mascherato solo su errore). Disattivata finché non acconsenti.',
    policy: 'Cookie Policy',
    alwaysOn: 'Sempre attivi',
  },
  en: {
    bannerTitle: 'Cookie preferences',
    bannerBody:
      'We use necessary cookies for security, login, and service operation. You can choose whether to enable functional preferences and optional categories.',
    reject: 'Reject all',
    customize: 'Manage preferences',
    accept: 'Accept all',
    dialogTitle: 'Manage cookie preferences',
    dialogBody:
      'Necessary cookies are always active. Other categories will only be used if enabled.',
    save: 'Save preferences',
    necessary: 'Necessary',
    necessaryBody: 'Login, security, fraud prevention, load balancing, and payment when requested.',
    functional: 'Functional',
    functionalBody: 'Remember language and interface preferences to improve the experience.',
    analytics: 'Analytics',
    analyticsBody: 'Enables Sentry error diagnostics in the browser (masked replay on errors only). Disabled until you opt in.',
    policy: 'Cookie Policy',
    alwaysOn: 'Always on',
  },
} as const;

export function CookieConsentManager() {
  const { language } = useLanguage();
  const copy = COOKIE_COPY[language === 'it' ? 'it' : 'en'];
  const [hasChoice, setHasChoice] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookieConsentPreferences>(DEFAULT_COOKIE_CONSENT);

  useEffect(() => {
    const stored = readCookieConsent();
    setHasChoice(Boolean(stored));
    if (stored) setPreferences(stored);
  }, []);

  useEffect(() => {
    const openPreferences = () => {
      if (!readCookieConsent()) {
        setPreferences({ ...DEFAULT_COOKIE_CONSENT });
      }
      setDialogOpen(true);
    };
    window.addEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, openPreferences);
  }, []);

  useEffect(() => {
    if (dialogOpen && !readCookieConsent()) {
      setPreferences({ ...DEFAULT_COOKIE_CONSENT });
    }
  }, [dialogOpen]);

  const persist = (next: CookieConsentPreferences) => {
    writeCookieConsent(next);
    setPreferences(next);
    setHasChoice(true);
    setDialogOpen(false);
  };

  const setOptional = (key: OptionalCookieKey, checked: boolean) => {
    setPreferences((current) => ({ ...current, [key]: checked }));
  };

  return (
    <>
      {!hasChoice && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border/60 bg-background/95 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="container flex flex-col gap-4 py-4 md:flex-row md:items-center md:justify-between">
            <div className="max-w-3xl space-y-1">
              <p className="text-sm font-semibold text-foreground">{copy.bannerTitle}</p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                {copy.bannerBody}{' '}
                <Link to="/cookies" className="text-primary underline-offset-4 hover:underline">
                  {copy.policy}
                </Link>
              </p>
            </div>
            <div className="flex w-full flex-col gap-2 sm:flex-row md:max-w-xl md:shrink-0">
              <Button
                variant="secondary"
                className="flex-1 min-h-10"
                onClick={() => persist({ ...DEFAULT_COOKIE_CONSENT })}
              >
                {copy.reject}
              </Button>
              <Button
                variant="secondary"
                className="flex-1 min-h-10"
                onClick={() =>
                  persist({
                    ...DEFAULT_COOKIE_CONSENT,
                    functional: true,
                    analytics: true,
                  })
                }
              >
                {copy.accept}
              </Button>
              <Button variant="outline" className="flex-1 min-h-10 sm:max-w-[11rem]" onClick={() => setDialogOpen(true)}>
                <SlidersHorizontal className="h-4 w-4 shrink-0" />
                {copy.customize}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{copy.dialogTitle}</DialogTitle>
            <DialogDescription>{copy.dialogBody}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <CookiePreferenceRow title={copy.necessary} body={copy.necessaryBody} locked label={copy.alwaysOn} />
            <CookiePreferenceRow
              title={copy.functional}
              body={copy.functionalBody}
              checked={preferences.functional}
              onCheckedChange={(checked) => setOptional('functional', checked)}
            />
            <CookiePreferenceRow
              title={copy.analytics}
              body={copy.analyticsBody}
              checked={preferences.analytics}
              onCheckedChange={(checked) => setOptional('analytics', checked)}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => persist(preferences)}>{copy.save}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CookiePreferenceRow({
  title,
  body,
  locked,
  label,
  checked,
  onCheckedChange,
}: {
  title: string;
  body: string;
  locked?: boolean;
  label?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-md border border-border/60 p-4">
      <div className="space-y-1">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
      </div>
      {locked ? (
        <span className="shrink-0 rounded-md bg-muted px-2 py-1 text-xs font-medium text-muted-foreground">{label}</span>
      ) : (
        <Switch checked={checked} onCheckedChange={onCheckedChange} aria-label={title} />
      )}
    </div>
  );
}